import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Trash2, RotateCcw, AlertTriangle, Eye, FileText, Download } from 'lucide-react';

export default function TrashManagement() {
  const { getAuthHeaders, user } = useAuth();
  const [trashedApps, setTrashedApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);
  const [appToAction, setAppToAction] = useState(null);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchTrashedApplications();
  }, []);

  const fetchTrashedApplications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/trash/applications`, { headers: getAuthHeaders() });
      setTrashedApps(response.data);
    } catch (error) {
      toast.error('Failed to fetch trashed applications');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (appId) => {
    try {
      const response = await axios.get(`${API_URL}/applications/${appId}`, { headers: getAuthHeaders() });
      setSelectedApp(response.data);
      setIsDetailsOpen(true);
    } catch (error) {
      toast.error('Failed to fetch application details');
    }
  };

  const handleRestoreClick = (app) => {
    setAppToAction(app);
    setIsRestoreConfirmOpen(true);
  };

  const handlePermanentDeleteClick = (app) => {
    setAppToAction(app);
    setIsDeleteConfirmOpen(true);
  };

  const handleRestore = async () => {
    try {
      await axios.put(`${API_URL}/trash/applications/${appToAction.id}/restore`, {}, { headers: getAuthHeaders() });
      toast.success('Application restored successfully');
      setIsRestoreConfirmOpen(false);
      setAppToAction(null);
      fetchTrashedApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore application');
    }
  };

  const handlePermanentDelete = async () => {
    try {
      await axios.delete(`${API_URL}/trash/applications/${appToAction.id}/permanent`, { headers: getAuthHeaders() });
      toast.success('Application permanently deleted');
      setIsDeleteConfirmOpen(false);
      setAppToAction(null);
      fetchTrashedApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to permanently delete application');
    }
  };

  const handleDownloadPDF = async (appId) => {
    try {
      const response = await axios.get(`${API_URL}/applications/${appId}/download-pdf`, { headers: getAuthHeaders() });
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.content}`;
      link.download = response.data.filename;
      link.click();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const InfoRow = ({ label, value }) => (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-slate-100">
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className="col-span-2 text-sm text-slate-900">{value || '-'}</div>
    </div>
  );

  return (
    <Sidebar>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Trash2 className="w-7 h-7 inline-block mr-2 text-red-500" />
              Trash
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Deleted applications are stored here. Only Admin can permanently delete.
            </p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
            <span className="text-red-700 font-medium">{trashedApps.length}</span>
            <span className="text-red-600 text-sm ml-1">items in trash</span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="trash-table">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : trashedApps.length === 0 ? (
            <div className="p-12 text-center">
              <Trash2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Trash is empty</p>
              <p className="text-sm text-slate-400">Deleted applications will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">App No.</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Student</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">College</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Agency</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Deleted By</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Deleted At</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trashedApps.map((app) => (
                    <tr key={app.id} className="border-b border-slate-100 hover:bg-red-50/30" data-testid={`trash-row-${app.id}`}>
                      <td className="p-4 text-sm font-medium text-slate-700">{app.application_number}</td>
                      <td className="p-4 text-sm text-slate-600">{app.student_name}</td>
                      <td className="p-4 text-sm text-slate-600 max-w-[150px] truncate" title={app.college_name}>{app.college_name}</td>
                      <td className="p-4 text-sm text-slate-600">{app.agency_name}</td>
                      <td className="p-4 text-sm">
                        <div>
                          <span className="text-slate-700">{app.deleted_by_name || 'Unknown'}</span>
                          <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                            app.deleted_by_role === 'admin' ? 'bg-purple-100 text-purple-700' :
                            app.deleted_by_role === 'sub_admin' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {app.deleted_by_role?.replace('_', ' ').toUpperCase() || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-slate-500">{formatDate(app.deleted_at)}</td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetails(app.id)}
                            title="View Details"
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="w-4 h-4 text-slate-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadPDF(app.id)}
                            title="Download PDF"
                            className="h-8 w-8 p-0"
                          >
                            <FileText className="w-4 h-4 text-slate-600" />
                          </Button>
                          {(user?.role === 'admin' || user?.role === 'sub_admin') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRestoreClick(app)}
                              title="Restore"
                              className="h-8 w-8 p-0 hover:bg-green-100"
                              data-testid={`restore-btn-${app.id}`}
                            >
                              <RotateCcw className="w-4 h-4 text-green-600" />
                            </Button>
                          )}
                          {user?.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handlePermanentDeleteClick(app)}
                              title="Permanently Delete"
                              className="h-8 w-8 p-0 hover:bg-red-100"
                              data-testid={`permanent-delete-btn-${app.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* View Details Dialog */}
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="text-red-500">[DELETED]</span>
                Application Details - {selectedApp?.application_number}
              </DialogTitle>
            </DialogHeader>
            {selectedApp && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    This application is in trash. Deleted by <strong>{selectedApp.deleted_by_name}</strong> on {formatDate(selectedApp.deleted_at)}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-md font-semibold text-slate-900 mb-2 pb-1 border-b">Admission Details</h3>
                  <div className="grid grid-cols-2 gap-x-6">
                    <InfoRow label="College/University" value={selectedApp.college_name} />
                    <InfoRow label="Session" value={selectedApp.admission_session} />
                    <InfoRow label="Course" value={`${selectedApp.course} - ${selectedApp.specialization}`} />
                    <InfoRow label="Admission Type" value={selectedApp.admission_type} />
                  </div>
                </div>

                <div>
                  <h3 className="text-md font-semibold text-slate-900 mb-2 pb-1 border-b">Personal Details</h3>
                  <div className="grid grid-cols-2 gap-x-6">
                    <InfoRow label="Student Name" value={selectedApp.student_name} />
                    <InfoRow label="Father's Name" value={selectedApp.father_name} />
                    <InfoRow label="Mother's Name" value={selectedApp.mother_name} />
                    <InfoRow label="Email" value={selectedApp.email} />
                    <InfoRow label="Mobile" value={selectedApp.mobile} />
                    <InfoRow label="DOB" value={selectedApp.dob} />
                    <InfoRow label="Gender" value={selectedApp.gender} />
                    <InfoRow label="Category" value={selectedApp.category} />
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Restore Confirmation Dialog */}
        <Dialog open={isRestoreConfirmOpen} onOpenChange={setIsRestoreConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-green-600" />
                Restore Application
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-slate-600">
                Are you sure you want to restore application <strong>{appToAction?.application_number}</strong>?
              </p>
              <p className="text-sm text-slate-500 mt-2">
                This will move the application back to the active applications list.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRestoreConfirmOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleRestore} 
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-restore-btn"
              >
                Restore Application
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Permanent Delete Confirmation Dialog */}
        <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Permanent Deletion
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-700 font-medium">⚠️ This action cannot be undone!</p>
              </div>
              <p className="text-slate-600">
                Are you sure you want to <strong>permanently delete</strong> application <strong>{appToAction?.application_number}</strong>?
              </p>
              <p className="text-sm text-slate-500 mt-2">
                All data and documents associated with this application will be permanently removed from the system.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</Button>
              <Button 
                onClick={handlePermanentDelete} 
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-permanent-delete-btn"
              >
                Permanently Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Sidebar>
  );
}
